import { TEXTOS } from '../lib/glossario'

/** Onde o backend começa o fluxo do Microsoft Entra ID. */
const ROTA_DE_LOGIN = '/api/v1/auth/microsoft/login'

/**
 * Manda o navegador para o login institucional — depois de confirmar que ele existe.
 *
 * O endpoint responde 302 para o tenant quando está configurado e **503 com corpo JSON**
 * quando não está (o ambiente de desenvolvimento não tem credencial do tenant da
 * Prefeitura). Navegar direto faria o usuário ver um JSON cru na tela nesse caso. Por
 * isso a sondagem com `redirect: 'manual'`: para um 302 de mesma origem o navegador
 * devolve uma resposta opaca, que é o sinal de "pode ir"; para o 503 devolve o corpo,
 * que dá para ler e traduzir.
 *
 * O custo é um pedido de autorização a mais aberto no servidor, que expira sozinho em
 * dez minutos e não é consumido por ninguém.
 *
 * @param destino caminho relativo para onde voltar depois de entrar
 * @returns nulo quando a navegação começou; a mensagem a exibir quando o login não é possível
 */
export async function iniciarLogin(destino: string): Promise<string | null> {
  const url = `${ROTA_DE_LOGIN}?destino=${encodeURIComponent(caminhoRelativo(destino))}`

  try {
    const resposta = await fetch(url, { redirect: 'manual', headers: { Accept: 'application/json' } })
    if (resposta.type === 'opaqueredirect' || resposta.ok) {
      window.location.assign(url)
      return null
    }
    return await traduzir(resposta)
  } catch {
    return 'Não foi possível falar com o servidor para iniciar o login. Verifique sua conexão e tente de novo.'
  }
}

async function traduzir(resposta: Response): Promise<string> {
  try {
    const problema = (await resposta.json()) as { codigo?: string; detail?: string }
    if (problema.codigo === 'LOGIN_NAO_CONFIGURADO') {
      return TEXTOS.loginNaoConfigurado
    }
    return problema.detail ?? TEXTOS.erroGenerico
  } catch {
    return TEXTOS.erroGenerico
  }
}

/**
 * Só caminho relativo simples vai como destino.
 *
 * O backend já recusa endereço absoluto — esta é a segunda barreira, do lado de cá, para
 * o caso de alguém montar o link de login à mão com `?destino=https://site-falso/`.
 *
 * @param destino candidato a destino
 * @returns o próprio caminho, ou a raiz quando ele não é seguro
 */
export function caminhoRelativo(destino: string | null | undefined): string {
  // A barra invertida entra na regra porque navegador normaliza `\` para `/` em URL:
  // `/\site-falso` viraria o protocol-relative `//site-falso` depois da barreira.
  if (!destino || !/^\/(?![/\\])/.test(destino)) {
    return '/'
  }
  return destino
}
